// ============================================================
// ChatPanel.js — AALI GROUP AI Chat Assistant
// Premium glassmorphism chat panel with floating buttons.
// Mobile: toggle button hides when panel is open (panel has its own close button)
// ============================================================

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { generateResponse, buildWhatsAppLink } from "./chatEngine";
import { QUICK_SUGGESTIONS, GREETING_MESSAGE, COMPANY_INFO } from "./chatData";
import "./ChatPanel.css";

// ── Markdown-style bold renderer (no external deps) ──────────
const renderText = (text) => {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part.split("\n").map((line, j, arr) => (
      <React.Fragment key={`${i}-${j}`}>
        {line}
        {j < arr.length - 1 && <br />}
      </React.Fragment>
    ));
  });
};

// ── Single Message Bubble ────────────────────────────────────
const MessageBubble = ({ message, onLinkClick }) => {
  const isBot = message.role === "bot";

  return (
    <div className={`ag-chat__bubble-wrap ${isBot ? "ag-chat__bubble-wrap--bot" : "ag-chat__bubble-wrap--user"}`}>
      {isBot && (
        <div className="ag-chat__avatar">
          <span>AG</span>
        </div>
      )}
      <div className={`ag-chat__bubble ${isBot ? "ag-chat__bubble--bot" : "ag-chat__bubble--user"}`}>
        <p className="ag-chat__text">{renderText(message.text)}</p>
        {isBot && message.links && message.links.length > 0 && (
          <div className="ag-chat__link-row">
            {message.links.map((link, i) =>
              link.whatsapp ? (
                <a
                  key={i}
                  href={buildWhatsAppLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ag-chat__link-btn ag-chat__link-btn--wa"
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  WhatsApp
                </a>
              ) : (
                <button
                  key={i}
                  onClick={() => onLinkClick(link.route)}
                  className="ag-chat__link-btn"
                >
                  {link.label}
                </button>
              )
            )}
          </div>
        )}
        <span className="ag-chat__time">{message.time}</span>
      </div>
    </div>
  );
};

// ── Typing Indicator ─────────────────────────────────────────
const TypingIndicator = () => (
  <div className="ag-chat__bubble-wrap ag-chat__bubble-wrap--bot">
    <div className="ag-chat__avatar"><span>AG</span></div>
    <div className="ag-chat__bubble ag-chat__bubble--bot ag-chat__bubble--typing">
      <span className="ag-chat__dot" />
      <span className="ag-chat__dot" />
      <span className="ag-chat__dot" />
    </div>
  </div>
);

// ── Main Chat Panel ──────────────────────────────────────────
const ChatPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputVal, setInputVal] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [hasGreeted, setHasGreeted] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const getTime = () =>
    new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, scrollToBottom]);

  // Detect mobile screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Show greeting when panel first opens
  useEffect(() => {
    if (isOpen && !hasGreeted) {
      setHasGreeted(true);
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        setMessages([
          {
            id: Date.now(),
            role: "bot",
            text: GREETING_MESSAGE,
            links: [],
            time: getTime(),
          },
        ]);
      }, 900);
    }
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen, hasGreeted]);

  const sendMessage = useCallback(
    (text) => {
      const trimmed = text.trim();
      if (!trimmed) return;

      setShowSuggestions(false);

      const userMsg = {
        id: Date.now(),
        role: "user",
        text: trimmed,
        time: getTime(),
      };
      setMessages((prev) => [...prev, userMsg]);
      setInputVal("");
      setIsTyping(true);

      // Simulate network/processing delay for natural feel
      const delay = 700 + Math.random() * 600;
      setTimeout(() => {
        const response = generateResponse(trimmed);
        const botMsg = {
          id: Date.now() + 1,
          role: "bot",
          text: response.text,
          links: response.links,
          time: getTime(),
        };
        setIsTyping(false);
        setMessages((prev) => [...prev, botMsg]);
      }, delay);
    },
    []
  );

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputVal);
    }
  };

  const handleLinkClick = (route) => {
    navigate(route);
    setIsOpen(false);
  };

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  // Decide whether to show the chat toggle button:
  // - On desktop/tablet, always show.
  // - On mobile, show only when panel is closed (because the panel has its own close button).
  const showChatToggle = !isMobile || (isMobile && !isOpen);

  return (
    <>
      

      {/* ── Chat Toggle Button (hidden on mobile when panel open) ── */}
      {showChatToggle && (
        <button
          className={`ag-float ag-float--chat ${isOpen ? "ag-float--active" : ""}`}
          onClick={handleToggle}
          aria-label={isOpen ? "Close chat" : "Open chat assistant"}
          title="AALI GROUP Assistant"
        >
          <span className={`ag-float__icon ag-float__icon--chat ${isOpen ? "ag-float__icon--hide" : "ag-float__icon--show"}`}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </span>
          <span className={`ag-float__icon ag-float__icon--close ${isOpen ? "ag-float__icon--show" : "ag-float__icon--hide"}`}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" width="20" height="20">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </span>
          <span className="ag-float__tooltip">Ask AALI GROUP</span>
        </button>
      )}

      {/* ── Chat Panel ── */}
      <div className={`ag-chat ${isOpen ? "ag-chat--open" : ""}`} role="dialog" aria-label="AALI GROUP Chat Assistant">
        {/* Header */}
        <div className="ag-chat__header">
          <div className="ag-chat__header-left">
            <div className="ag-chat__header-avatar">
              <span>AG</span>
              <span className="ag-chat__status-dot" />
            </div>
            <div>
              <p className="ag-chat__header-name">{COMPANY_INFO.name}</p>
              <p className="ag-chat__header-sub">AI Assistant • Online</p>
            </div>
          </div>
          <div className="ag-chat__header-actions">
            <a
              href={buildWhatsAppLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="ag-chat__header-btn"
              title="Continue on WhatsApp"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
            </a>
            <button className="ag-chat__header-btn" onClick={() => setIsOpen(false)} title="Close" aria-label="Close chat">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" width="16" height="16">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Messages Area */}
        <div className="ag-chat__messages">
          {messages.map((msg) => (
            <MessageBubble key={msg.id} message={msg} onLinkClick={handleLinkClick} />
          ))}
          {isTyping && <TypingIndicator />}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Suggestions */}
        {showSuggestions && messages.length > 0 && (
          <div className="ag-chat__suggestions">
            <p className="ag-chat__suggestions-label">Quick questions</p>
            <div className="ag-chat__suggestions-grid">
              {QUICK_SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  className="ag-chat__suggestion-chip"
                  onClick={() => sendMessage(s.query)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input Bar */}
        <div className="ag-chat__footer">
          <input
            ref={inputRef}
            className="ag-chat__input"
            type="text"
            placeholder="Type your message…"
            value={inputVal}
            onChange={(e) => setInputVal(e.target.value)}
            onKeyDown={handleKeyDown}
            maxLength={300}
            aria-label="Chat input"
          />
          <button
            className={`ag-chat__send ${inputVal.trim() ? "ag-chat__send--active" : ""}`}
            onClick={() => sendMessage(inputVal)}
            disabled={!inputVal.trim()}
            aria-label="Send message"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>

        {/* Powered by note */}
        <div className="ag-chat__powered">
          Powered by <strong>{COMPANY_INFO.name}</strong>
        </div>
      </div>

      {/* Backdrop (mobile only) */}
      {isOpen && <div className="ag-chat__backdrop" onClick={() => setIsOpen(false)} />}
    </>
  );
};

export default ChatPanel;